import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'adult-kuaishou:favorites';

function readFavorites(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

function writeFavorites(ids: string[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    window.dispatchEvent(new Event('favorites-updated'));
  } catch {
    /* ignore quota errors */
  }
}

export function useFavorites() {
  const [favoriteIds, setFavoriteIds] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const sync = () => setFavoriteIds(readFavorites());
    window.addEventListener('favorites-updated', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('favorites-updated', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const isFavorite = useCallback(
    (id: string) => favoriteIds.includes(id),
    [favoriteIds],
  );

  const toggleFavorite = useCallback((id: string) => {
    const current = readFavorites();
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [id, ...current];
    writeFavorites(next);
    setFavoriteIds(next);
    return next.includes(id);
  }, []);

  const removeFavorite = useCallback((id: string) => {
    const next = readFavorites().filter((x) => x !== id);
    writeFavorites(next);
    setFavoriteIds(next);
  }, []);

  const clearFavorites = useCallback(() => {
    writeFavorites([]);
    setFavoriteIds([]);
  }, []);

  return { favoriteIds, isFavorite, toggleFavorite, removeFavorite, clearFavorites };
}